import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency as formatCurrencyUtil } from '../../utils/currencyFormatter';
import { bundlePricingService } from '../../services/bundlePricingService';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import { Layers, Plus, Save, Trash2, Edit2, Package, Search, AlertCircle } from 'lucide-react';

const BundlePricingCalculator = () => {
  const { profile } = useAuth();
  const currency = profile?.currency || 'ZAR';
  
  const [activeTab, setActiveTab] = useState('create');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Current bundle being created/edited
  const [currentBundle, setCurrentBundle] = useState({
    id: null,
    name: '',
    products: [
      { id: Date.now(), name: '', cost: '', price: '', quantity: 1 },
    ],
    bundleDiscount: 15,
    notes: '',
  });

  // Saved bundles list - REMOVED mock data
  const [savedBundles, setSavedBundles] = useState([]);

  // Load bundles from Supabase on component mount
  useEffect(() => {
    loadBundles();
  }, []);

  const loadBundles = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await bundlePricingService?.getAll();
      
      // Convert database format to component format
      const formattedBundles = data?.map(bundle => ({
        id: bundle?.id,
        name: bundle?.bundleName,
        products: bundle?.items, // items from DB → products in component
        bundleDiscount: bundle?.bundleDiscountPercentage,
        notes: bundle?.bundleDescription || '',
        createdAt: new Date(bundle.createdAt)?.toISOString()?.split('T')?.[0],
        updatedAt: bundle?.updatedAt ? new Date(bundle.updatedAt)?.toISOString()?.split('T')?.[0] : null,
      }));
      
      setSavedBundles(formattedBundles);
    } catch (err) {
      console.error('Error loading bundles:', err);
      setError(err?.message || 'Failed to load bundles');
    } finally {
      setLoading(false);
    }
  };

  const parseNum = (val) => {
    const num = parseFloat(String(val)?.replace(/[^\d.-]/g, ''));
    return isNaN(num) ? 0 : num;
  };

  const formatCurrency = (amount) => {
    // Use the working utility function directly
    return formatCurrencyUtil(amount || 0, currency);
  };

  const calculateBundleMetrics = (bundle) => {
    let totalCost = 0;
    let totalPrice = 0;
    
    bundle?.products?.forEach(product => {
      const cost = parseNum(product?.cost) * parseNum(product?.quantity);
      const price = parseNum(product?.price) * parseNum(product?.quantity);
      totalCost += cost;
      totalPrice += price;
    });
    
    const bundleDiscount = parseNum(bundle?.bundleDiscount);
    const bundlePrice = totalPrice * (1 - bundleDiscount / 100);
    const bundleMargin = totalCost > 0 ? ((bundlePrice - totalCost) / bundlePrice) * 100 : 0;
    const savings = totalPrice - bundlePrice;
    const profit = bundlePrice - totalCost;
    
    return {
      totalCost,
      totalPrice,
      bundlePrice,
      bundleMargin,
      savings,
      profit,
    };
  };

  const addProduct = () => {
    const newProduct = {
      id: Date.now(),
      name: '',
      cost: '',
      price: '',
      quantity: 1,
    };
    setCurrentBundle(prev => ({
      ...prev,
      products: [...prev?.products, newProduct],
    }));
  };

  const removeProduct = (productId) => {
    if (currentBundle?.products?.length <= 1) return;
    setCurrentBundle(prev => ({
      ...prev,
      products: prev?.products?.filter(p => p?.id !== productId),
    }));
  };

  const updateProduct = (productId, field, value) => {
    setCurrentBundle(prev => ({
      ...prev,
      products: prev?.products?.map(p =>
        p?.id === productId ? { ...p, [field]: value } : p
      ),
    }));
  };

  const saveBundle = async () => {
    if (!currentBundle?.name?.trim()) {
      setError('Please enter a bundle name');
      return;
    }

    const hasEmptyProducts = currentBundle?.products?.some(
      p => !p?.name?.trim() || !p?.cost || !p?.price
    );

    if (hasEmptyProducts) {
      setError('Please fill in all product details');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');

      const metrics = calculateBundleMetrics(currentBundle);

      // Prepare data for database (camelCase → snake_case handled by service)
      const bundleData = {
        bundleName: currentBundle?.name,
        bundleDescription: currentBundle?.notes || '',
        totalIndividualPrice: metrics?.totalPrice,
        bundleDiscountPercentage: parseNum(currentBundle?.bundleDiscount),
        bundlePrice: metrics?.bundlePrice,
        savingsAmount: metrics?.savings,
        items: currentBundle?.products, // Will be stored as JSONB
      };

      if (currentBundle?.id) {
        // Update existing bundle
        await bundlePricingService?.update(currentBundle?.id, bundleData);
        setSuccessMessage('Bundle updated successfully!');
      } else {
        // Create new bundle
        await bundlePricingService?.create(bundleData);
        setSuccessMessage('Bundle saved successfully!');
      }

      // Reload bundles from database
      await loadBundles();

      // Reset form
      setCurrentBundle({
        id: null,
        name: '',
        products: [{ id: Date.now(), name: '', cost: '', price: '', quantity: 1 }],
        bundleDiscount: 15,
        notes: '',
      });

      // Switch to saved tab to show the new bundle
      setActiveTab('saved');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving bundle:', err);
      setError(err?.message || 'Failed to save bundle');
    } finally {
      setLoading(false);
    }
  };

  const editBundle = (bundle) => {
    setCurrentBundle({ ...bundle });
    setActiveTab('create');
    setError('');
    setSuccessMessage('');
  };

  const deleteBundle = async (bundleId) => {
    if (!window.confirm('Are you sure you want to delete this bundle?')) return;

    try {
      setLoading(true);
      setError('');
      await bundlePricingService?.delete(bundleId);
      setSuccessMessage('Bundle deleted successfully!');
      
      // Reload bundles
      await loadBundles();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting bundle:', err);
      setError(err?.message || 'Failed to delete bundle');
    } finally {
      setLoading(false);
    }
  };

  const duplicateBundle = (bundle) => {
    const duplicated = {
      ...bundle,
      id: null,
      name: `${bundle?.name} (Copy)`,
      products: bundle?.products?.map(p => ({ ...p, id: Date.now() + Math.random() })),
    };
    setCurrentBundle(duplicated);
    setActiveTab('create');
    setError('');
    setSuccessMessage('');
  };

  const filteredBundles = savedBundles?.filter(bundle =>
    bundle?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const renderCreateTab = () => {
    const metrics = calculateBundleMetrics(currentBundle);

    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Bundle Creation Panel (70%) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Bundle Name */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Bundle Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Bundle Name *
                </label>
                <input
                  type="text"
                  value={currentBundle?.name}
                  onChange={(e) => setCurrentBundle(prev => ({ ...prev, name: e?.target?.value }))}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="e.g., Summer Sale Bundle, Starter Package"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={currentBundle?.notes}
                  onChange={(e) => setCurrentBundle(prev => ({ ...prev, notes: e?.target?.value }))}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Add any notes about this bundle..."
                  rows="2"
                />
              </div>
            </div>
          </div>

          {/* Products Section */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">Bundle Products</h3>
              <button
                onClick={addProduct}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Product
              </button>
            </div>

            <div className="space-y-4">
              {currentBundle?.products?.map((product, index) => (
                <div key={product?.id} className="p-4 border border-slate-200 rounded-lg bg-slate-50">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="font-medium text-slate-900">Product {index + 1}</h4>
                    {currentBundle?.products?.length > 1 && (
                      <button
                        onClick={() => removeProduct(product?.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="col-span-2">
                      <label className="block text-xs text-slate-600 mb-1">Product Name *</label>
                      <input
                        type="text"
                        value={product?.name}
                        onChange={(e) => updateProduct(product?.id, 'name', e?.target?.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                        placeholder="Product name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-600 mb-1">Cost *</label>
                      <input
                        type="text"
                        value={product?.cost}
                        onChange={(e) => updateProduct(product?.id, 'cost', e?.target?.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-600 mb-1">Price *</label>
                      <input
                        type="text"
                        value={product?.price}
                        onChange={(e) => updateProduct(product?.id, 'price', e?.target?.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                        placeholder="0.00"
                      />
                    </div>
                    <div className="col-span-2 md:col-span-4">
                      <label className="block text-xs text-slate-600 mb-1">
                        Quantity: {product?.quantity}
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="20"
                        value={product?.quantity}
                        onChange={(e) => updateProduct(product?.id, 'quantity', e?.target?.value)}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>1</span>
                        <span>10</span>
                        <span>20</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Line Total:</span>
                      <span className="font-semibold text-slate-900">
                        {formatCurrency(parseNum(product?.price) * parseNum(product?.quantity))}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bundle Discount */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Bundle Discount</h3>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Discount: {currentBundle?.bundleDiscount}%
              </label>
              <input
                type="range"
                min="0"
                max="50"
                value={currentBundle?.bundleDiscount}
                onChange={(e) => setCurrentBundle(prev => ({ ...prev, bundleDiscount: e?.target?.value }))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={saveBundle}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-5 h-5" />
            {loading ? 'Saving...' : (currentBundle?.id ? 'Update Bundle' : 'Save Bundle')}
          </button>
        </div>

        {/* Right: Analytics Sidebar (30%) */}
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 sticky top-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Bundle Analytics</h3>
            
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-600 mb-1">Individual Total</p>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(metrics?.totalPrice)}</p>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-900 mb-1">Bundle Price</p>
                <p className="text-3xl font-bold text-blue-700">{formatCurrency(metrics?.bundlePrice)}</p>
                <p className="text-xs text-blue-600 mt-1">{currentBundle?.bundleDiscount}% discount</p>
              </div>

              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-900 mb-1">Customer Savings</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(metrics?.savings)}</p>
              </div>

              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-sm text-purple-900 mb-1">Bundle Margin</p>
                <p className="text-2xl font-bold text-purple-700">{metrics?.bundleMargin?.toFixed(2)}%</p>
              </div>

              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-orange-900 mb-1">Total Profit</p>
                <p className="text-2xl font-bold text-orange-700">{formatCurrency(metrics?.profit)}</p>
              </div>

              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-900 mb-1">Total Cost</p>
                <p className="text-xl font-bold text-yellow-700">{formatCurrency(metrics?.totalCost)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSavedTab = () => {
    return (
      <div className="space-y-6">
        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Search bundles..."
            />
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-slate-600">Loading bundles...</p>
          </div>
        )}

        {/* Bundles List */}
        {!loading && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredBundles?.map((bundle) => {
              const metrics = calculateBundleMetrics(bundle);
              return (
                <div key={bundle?.id} className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{bundle?.name}</h3>
                      <p className="text-xs text-slate-500 mt-1">
                        Created: {bundle?.createdAt}
                        {bundle?.updatedAt && ` • Updated: ${bundle?.updatedAt}`}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => editBundle(bundle)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => duplicateBundle(bundle)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Duplicate"
                      >
                        <Package className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteBundle(bundle?.id)}
                        disabled={loading}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {bundle?.notes && (
                    <p className="text-sm text-slate-600 mb-4 italic">{bundle?.notes}</p>
                  )}

                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Products:</span>
                      <span className="font-semibold text-slate-900">
                        {bundle?.products?.length} items
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Bundle Price:</span>
                      <span className="font-semibold text-blue-700">
                        {formatCurrency(metrics?.bundlePrice)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Customer Savings:</span>
                      <span className="font-semibold text-green-700">
                        {formatCurrency(metrics?.savings)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Margin:</span>
                      <span className="font-semibold text-purple-700">
                        {metrics?.bundleMargin?.toFixed(2)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Profit:</span>
                      <span className="font-semibold text-orange-700">
                        {formatCurrency(metrics?.profit)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <p className="text-xs text-slate-600 mb-2">Products in bundle:</p>
                    <div className="space-y-1">
                      {bundle?.products?.slice(0, 3)?.map((product, idx) => (
                        <div key={idx} className="text-xs text-slate-700">
                          • {product?.name} (x{product?.quantity}) - {formatCurrency(parseNum(product?.price) * parseNum(product?.quantity))}
                        </div>
                      ))}
                      {bundle?.products?.length > 3 && (
                        <p className="text-xs text-slate-500 italic">
                          +{bundle?.products?.length - 3} more products
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && filteredBundles?.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
            <Layers className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {searchQuery ? 'No bundles found' : 'No saved bundles yet'}
            </h3>
            <p className="text-slate-600 mb-6">
              {searchQuery
                ? 'Try adjusting your search query' :'Create your first bundle to get started'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => setActiveTab('create')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Plus className="w-5 h-5" />
                Create Bundle
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <Header />
      <main className="ml-64 mt-16 p-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
              <Layers className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Bundle Pricing Calculator</h1>
              <p className="text-sm text-slate-600">
                Create, manage, and optimize product bundle pricing strategies
              </p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">Error</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-xs">✓</span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-green-900">Success</p>
              <p className="text-sm text-green-700 mt-1">{successMessage}</p>
            </div>
            <button
              onClick={() => setSuccessMessage('')}
              className="text-green-600 hover:text-green-800"
            >
              ×
            </button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 mb-6">
          <div className="flex">
            <button
              onClick={() => setActiveTab('create')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-all border-b-2 ${
                activeTab === 'create' ?'border-blue-500 text-blue-700 bg-blue-50' :'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Plus className="w-4 h-4" />
                <span>{currentBundle?.id ? 'Edit Bundle' : 'Create Bundle'}</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('saved')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-all border-b-2 ${
                activeTab === 'saved' ?'border-blue-500 text-blue-700 bg-blue-50' :'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Package className="w-4 h-4" />
                <span>Saved Bundles ({savedBundles?.length})</span>
              </div>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'create' && renderCreateTab()}
          {activeTab === 'saved' && renderSavedTab()}
        </div>
      </main>
    </div>
  );
};

export default BundlePricingCalculator;